import API from '@apis/client';
import { AxiosResponse } from 'axios';
import {
  createPurchaseReRequestPayload,
  deletePurchasePayload,
  getPurchaseDetailPayload,
  getPurchaseItemPayload,
  getPurchasePayload,
} from 'models/Supplement';
import {
  QuotationPurchaseGetPurchaseQuotationSelfData,
  QuotationPurchaseGetPurchaseQuotationSelfError,
  QuotationPurchasePostPurchaseQuotationByCartData,
  QuotationPurchasePostPurchaseQuotationByCartError,
  QuotationPurchasePostPurchaseQuotationByCartPayload,
  QuotationPurchasePostPurchaseQuotationByFileData,
  QuotationPurchasePostPurchaseQuotationByFileError,
  QuotationPurchasePostPurchaseQuotationByFilePayload,
  QuotationPurchaseSelfDeleteData,
  QuotationPurchaseSelfDeleteError,
  QuotationPurchaseSelfGetPurchaseQuotationItemSelfData,
  QuotationPurchaseSelfGetPurchaseQuotationItemSelfError,
  QuotationPurchaseSelfPostPurchaseQuotationByBiddingData,
  QuotationPurchaseSelfPostPurchaseQuotationByBiddingError,
  QuotationPurchaseSelfPostPurchaseQuotationReRequestData,
  QuotationPurchaseSelfPostPurchaseQuotationReRequestError,
  QuotationPurchaseSelfReadData,
  QuotationPurchaseSelfReadError,
} from 'models/swagger/data-contracts';

/**
 *  구매자 - 구매 견적 요청서 생성 API - 파일
 *  @function createPurchaseFileApi
 *  @param {string} kind 입찰용 / 구매용
 *  @param {string} quotation_file 요청 파일
 *  @param {string} quotation_file_name 파일 명
 *  @param {string} project_name 프로젝트 명
 *  @param {string} end_customer 최종수요자
 *  @param {string} req_close_at 견적 마감일자
 *  @param {boolean} is_consultable 협의 가능
 *  @param {string} req_delivery_at 요청 납기일자
 *  @param {string} restricted_country 원산지 제한 국가
 *  @param {boolean} is_restricted_country 원산지 제한 국가 없음
 *  @param {string} terms_of_payment 거래조건
 *  @param {boolean} is_loading 상차도 여부
 *  @param {string} shipping_area 납품지역
 *  @param {string} approved_vendor_list_file 승인 업체 리스트
 *  @param {string} approved_vendor_list_file_name 승인 업체 리스트 파일 명
 *  @param {boolean} is_approved_vendor_list_file 승인 업체 리스트 없음
 *  @param {string} additional_request 추가 요청사항
 *  @param {string} additional_request_file_name 추가 요청사항 파일 명
 *  @param {string} is_additional_request_file 추가 요청사항 text 여부
 */
export const createPurchaseFileApi = (
  purchaseByFilePayload: QuotationPurchasePostPurchaseQuotationByFilePayload,
): Promise<
  AxiosResponse<QuotationPurchasePostPurchaseQuotationByFileData, QuotationPurchasePostPurchaseQuotationByFileError>
> => {
  return API.post(`/quotation/purchase/file`, purchaseByFilePayload);
};

/**
 *  구매자 - 구매 견적 요청서 생성 API - 카트
 *  @function createPurchaseCartApi
 *  @param {string} kind 입찰용 / 구매용
 *  @param {number[]} cart_ids 카트 id 배열
 *  @param {string} project_name 프로젝트 명
 *  @param {string} end_customer 최종수요자
 *  @param {string} req_close_at 견적 마감일자
 *  @param {boolean} is_consultable 협의 가능
 *  @param {string} req_delivery_at 요청 납기일자
 *  @param {string} restricted_country 원산지 제한 국가
 *  @param {boolean} is_restricted_country 원산지 제한 국가 없음
 *  @param {string} terms_of_payment 거래조건
 *  @param {string} is_loading 상차도 여부
 *  @param {string} shipping_area 납품지역
 *  @param {string} approved_vendor_list_file 승인 업체 리스트
 *  @param {string} approved_vendor_list_file_name 승인 업체 리스트 파일 명
 *  @param {boolean} is_approved_vendor_list_file 승인 업체 리스트 없음
 *  @param {string} additional_request 추가 요청사항
 *  @param {string} additional_request_file_name 추가 요청사항 파일 명
 *  @param {string} is_additional_request_file 추가 요청사항 text 여부
 */
export const createPurchaseCartApi = (
  purchaseCartPayload: QuotationPurchasePostPurchaseQuotationByCartPayload,
): Promise<
  AxiosResponse<QuotationPurchasePostPurchaseQuotationByCartData, QuotationPurchasePostPurchaseQuotationByCartError>
> => {
  return API.post(`/quotation/purchase/cart`, purchaseCartPayload);
};

/**
 *  구매자 - 자신의 구매 견적 요청서 리스트 가져오기 API
 *  @function getPurchaseApi
 *  @param {number} page 페이지
 *  @param {string} kind 타입 (BID / BUY)
 *  @param {string} project_name 프로젝트 명
 *  @param {string} rfq_number 접수번호
 *  @param {boolean} finish 완료 여부
 *  @param {string} sub_kind 타입 (BID_PURCHASE / BID_BUDGET)
 */
export const getPurchaseApi = ({
  page,
  kind,
  sub_kind,
  finish,
  project_name,
  rfq_number,
}: getPurchasePayload): Promise<
  AxiosResponse<QuotationPurchaseGetPurchaseQuotationSelfData, QuotationPurchaseGetPurchaseQuotationSelfError>
> => {
  return API.get(
    `/quotation/purchase/self?page=${page}&kind=${kind}&finish=${finish}&project_name=${project_name}&rfq_number=${rfq_number}&sub_kind=${sub_kind}`,
  );
};

/**
 *  구매자 - 자신의 구매 견적 요청서 상세 조회 API
 *  @function getPurchaseDetailApi
 *  @param {number} rfq_number_id rfq number id
 */
export const getPurchaseDetailApi = ({
  rfq_number_id,
}: getPurchaseDetailPayload): Promise<AxiosResponse<QuotationPurchaseSelfReadData, QuotationPurchaseSelfReadError>> => {
  return API.get(`/quotation/purchase/self/${rfq_number_id}`);
};

/**
 *  구매자 - 자신의 구매 견적 요청서 자재 리스트 조회 API
 *  @function getPurchaseItemApi
 *  @param {number} rfq_number_id rfq number id
 */
export const getPurchaseItemApi = ({
  rfq_number_id,
}: getPurchaseItemPayload): Promise<
  AxiosResponse<
    QuotationPurchaseSelfGetPurchaseQuotationItemSelfData,
    QuotationPurchaseSelfGetPurchaseQuotationItemSelfError
  >
> => {
  return API.get(`/quotation/purchase/self/${rfq_number_id}/item`);
};

/**
 *  구매자 - 수정요청된 견적서 재요청 API
 *  @function createPurchaseReRequestApi
 *  @param {number} rfq_number_id rfq number id
 *  @param {string} kind 입찰용 / 구매용
 *  @param {string} type 카트 / 파일
 *  @param {number[]} item_ids 카트 id 배열
 *  @param {string} quotation_file 요청 파일
 *  @param {string} quotation_file_name 요청 파일 명
 *  @param {string} project_name 프로젝트 명
 *  @param {string} end_customer 최종수요자
 *  @param {string} req_close_at 견적 마감일자
 *  @param {boolean} is_consultable 협의 가능
 *  @param {string} req_delivery_at 요청 납기일자
 *  @param {string} restricted_country 원산지 제한 국가
 *  @param {boolean} is_restricted_country 원산지 제한 국가 없음
 *  @param {string} terms_of_payment 거래조건
 *  @param {string} is_loading 상차도 여부
 *  @param {string} shipping_area 납품지역
 *  @param {string} approved_vendor_list_file 승인 업체 리스트
 *  @param {string} approved_vendor_list_file_name 승인 업체 리스트 파일 명
 *  @param {boolean} is_approved_vendor_list_file 승인 업체 리스트 없음
 *  @param {string} additional_request 추가 요청사항
 *  @param {string} additional_request_file_name 추가 요청사항 파일 명
 *  @param {string} is_additional_request_file 추가 요청사항 text 여부
 */
export const createPurchaseReRequestApi = ({
  rfq_number_id,
  ...rest
}: createPurchaseReRequestPayload): Promise<
  AxiosResponse<
    QuotationPurchaseSelfPostPurchaseQuotationReRequestData,
    QuotationPurchaseSelfPostPurchaseQuotationReRequestError
  >
> => {
  return API.post(`/quotation/purchase/self/${rfq_number_id}/re-request`, {
    rfq_number_id,
    ...rest,
  });
};

/**
 *  구매자 - 자신의 구매 견적 요청서 삭제 API
 *  @function deletePurchaseApi
 *  @param {number} rfq_number_id rfq number id
 */
export const deletePurchaseApi = ({
  rfq_number_id,
}: deletePurchasePayload): Promise<
  AxiosResponse<QuotationPurchaseSelfDeleteData, QuotationPurchaseSelfDeleteError>
> => {
  return API.delete(`/quotation/purchase/self/${rfq_number_id}`);
};

/**
 *  구매자 - 입찰용 -> 구매용 견적 재요청 API
 *  @function createPurchaseByBidApi
 *  @param {number} rfq_number_id rfq number id
 *  @param {string} kind 입찰용 / 구매용
 *  @param {string} type 카트 / 파일
 *  @param {number[]} item_ids 카트 id 배열
 *  @param {string} quotation_file 요청 파일
 *  @param {string} quotation_file_name 요청 파일 명
 *  @param {string} project_name 프로젝트 명
 *  @param {string} end_customer 최종수요자
 *  @param {string} req_close_at 견적 마감일자
 *  @param {boolean} is_consultable 협의 가능
 *  @param {string} req_delivery_at 요청 납기일자
 *  @param {string} restricted_country 원산지 제한 국가
 *  @param {boolean} is_restricted_country 원산지 제한 국가 없음
 *  @param {string} terms_of_payment 거래조건
 *  @param {string} is_loading 상차도 여부
 *  @param {string} shipping_area 납품지역
 *  @param {string} approved_vendor_list_file 승인 업체 리스트
 *  @param {string} approved_vendor_list_file_name 승인 업체 리스트 파일 명
 *  @param {boolean} is_approved_vendor_list_file 승인 업체 리스트 없음
 *  @param {string} additional_request 추가 요청사항
 *  @param {string} additional_request_file_name 추가 요청사항 파일 명
 *  @param {string} is_additional_request_file 추가 요청사항 text 여부
 */
export const createPurchaseByBidApi = ({
  rfq_number_id,
  kind,
  type,
  item_ids,
  quotation_file,
  quotation_file_name,
  project_name,
  end_customer,
  req_close_at,
  is_consultable,
  req_delivery_at,
  restricted_country,
  is_restricted_country,
  terms_of_payment,
  is_loading,
  shipping_area,
  approved_vendor_list,
  approved_vendor_list_file,
  approved_vendor_list_file_name,
  is_approved_vendor_list_file,
  additional_request,
  additional_request_file,
  additional_request_file_name,
  is_additional_request_file,
}: createPurchaseReRequestPayload): Promise<
  AxiosResponse<
    QuotationPurchaseSelfPostPurchaseQuotationByBiddingData,
    QuotationPurchaseSelfPostPurchaseQuotationByBiddingError
  >
> => {
  return API.post(`/quotation/purchase/self/${rfq_number_id}/by-bidding`, {
    kind,
    type,
    item_ids,
    quotation_file,
    quotation_file_name,
    project_name,
    end_customer,
    req_close_at,
    is_consultable,
    req_delivery_at,
    restricted_country,
    is_restricted_country,
    terms_of_payment,
    is_loading,
    shipping_area,
    approved_vendor_list,
    approved_vendor_list_file,
    approved_vendor_list_file_name,
    is_approved_vendor_list_file,
    additional_request,
    additional_request_file,
    additional_request_file_name,
    is_additional_request_file,
  });
};
